import React, { useState, useMemo } from 'react';
import { Layout } from './Layout';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from './ui/breadcrumb';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { DateRangeFilter, DateRange } from './DateRangeFilter';
import { 
  Home, 
  Shield, 
  Server, 
  Download, 
  RefreshCw,
  Cpu,
  Database,
  HardDrive,
  Network
} from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';

const generatePerformanceData = () => {
  const now = new Date();
  return Array.from({ length: 24 }, (_, i) => {
    const time = new Date(now.getTime() - (23 - i) * 60 * 60 * 1000);
    return {
      time: time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
      timestamp: time,
      cpu: Math.random() * 80 + 20,
      memory: Math.random() * 90 + 10,
      disk: Math.random() * 60 + 10,
      network: Math.random() * 100 + 50,
      responseTime: Math.random() * 500 + 100,
      throughput: Math.random() * 1000 + 500,
    };
  });
};

export function PerformanceMonitor() {
  const [performanceData] = useState(generatePerformanceData());
  const [dateRange, setDateRange] = useState<DateRange>({
    from: new Date(Date.now() - 24 * 60 * 60 * 1000),
    to: new Date(),
  });

  const currentMetrics = useMemo(() => {
    const latest = performanceData[performanceData.length - 1];
    return {
      cpu: latest?.cpu.toFixed(1) || '0',
      memory: latest?.memory.toFixed(1) || '0',
      disk: latest?.disk.toFixed(1) || '0',
      network: latest?.network.toFixed(0) || '0',
      responseTime: latest?.responseTime.toFixed(0) || '0',
      throughput: latest?.throughput.toFixed(0) || '0',
    };
  }, [performanceData]);

  const handleRefresh = () => {
    toast.success('Performance data refreshed');
  };

  const handleExport = () => {
    toast.success('Performance report exported');
  };

  return (
    <Layout>
      <div className="h-full flex flex-col" style={{ fontSize: '11.2px' }}>
        <div className="p-8 pb-4">
          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2">
                  <Home className="w-3 h-3" />
                  Dashboard
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbLink href="#" className="flex items-center gap-2">
                  <Shield className="w-3 h-3" />
                  Admin Audit
                </BreadcrumbLink>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>Performance Monitor</BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>

        <div className="px-8 pb-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h1 className="text-gray-900 mb-2" style={{ fontSize: '19.6px', fontWeight: 600 }}>
                Performance Monitor
              </h1>
              <p className="text-gray-600" style={{ fontSize: '11.2px' }}>
                Real-time system performance and health monitoring
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={handleRefresh}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
              <Button variant="outline" onClick={handleExport}>
                <Download className="w-4 h-4 mr-2" />
                Export
              </Button>
            </div>
          </div>

          {/* Current Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-6 gap-4 mb-6">
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Cpu className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {currentMetrics.cpu}%
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>CPU Usage</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Database className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {currentMetrics.memory}%
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Memory</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <HardDrive className="w-8 h-8 text-yellow-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {currentMetrics.disk}%
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Disk Usage</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Network className="w-8 h-8 text-purple-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {currentMetrics.network}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Network MB/s</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Server className="w-8 h-8 text-orange-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {currentMetrics.responseTime}ms
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Response Time</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <Server className="w-8 h-8 text-red-500" />
                  <div>
                    <p className="text-gray-900" style={{ fontSize: '18px', fontWeight: 600 }}>
                      {currentMetrics.throughput}
                    </p>
                    <p className="text-gray-600" style={{ fontSize: '10px' }}>Requests/min</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Performance Charts */}
        <div className="px-8 pb-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>System Resources (24 Hours)</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="time" stroke="#6B7280" fontSize={9.6} />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Line type="monotone" dataKey="cpu" stroke="#3B82F6" strokeWidth={2} name="CPU %" />
                      <Line type="monotone" dataKey="memory" stroke="#10B981" strokeWidth={2} name="Memory %" />
                      <Line type="monotone" dataKey="disk" stroke="#F59E0B" strokeWidth={2} name="Disk %" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle style={{ fontSize: '14px' }}>Application Performance</CardTitle>
              </CardHeader>
              <CardContent>
                <div style={{ height: '300px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={performanceData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                      <XAxis dataKey="time" stroke="#6B7280" fontSize={9.6} />
                      <YAxis stroke="#6B7280" fontSize={9.6} />
                      <Tooltip contentStyle={{ fontSize: '11.2px' }} />
                      <Line type="monotone" dataKey="responseTime" stroke="#EF4444" strokeWidth={2} name="Response Time (ms)" />
                      <Line type="monotone" dataKey="throughput" stroke="#8B5CF6" strokeWidth={2} name="Throughput" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* System Health Status */}
        <div className="flex-1 px-8 pb-8">
          <Card>
            <CardHeader>
              <CardTitle style={{ fontSize: '14px' }}>System Health Status</CardTitle>
              <CardDescription style={{ fontSize: '11.2px' }}>
                Current status of critical system components
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="text-green-900 mb-2" style={{ fontSize: '12px', fontWeight: 600 }}>
                    Application Services
                  </h4>
                  <Badge className="bg-green-100 text-green-800 mb-2">Healthy</Badge>
                  <p className="text-green-800" style={{ fontSize: '10px' }}>
                    All application services are running normally with good response times.
                  </p>
                </div>
                
                <div className="p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                  <h4 className="text-yellow-900 mb-2" style={{ fontSize: '12px', fontWeight: 600 }}>
                    Database Performance
                  </h4>
                  <Badge className="bg-yellow-100 text-yellow-800 mb-2">Warning</Badge>
                  <p className="text-yellow-800" style={{ fontSize: '10px' }}>
                    Database queries showing slightly elevated response times. Monitor closely.
                  </p>
                </div>
                
                <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                  <h4 className="text-green-900 mb-2" style={{ fontSize: '12px', fontWeight: 600 }}>
                    Network Infrastructure
                  </h4>
                  <Badge className="bg-green-100 text-green-800 mb-2">Healthy</Badge>
                  <p className="text-green-800" style={{ fontSize: '10px' }}>
                    Network connectivity and bandwidth utilization within normal ranges.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
}